import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { Button } from "~/components/button";
import { prop } from "~/tools/prop";

import { ZIndexBoundary } from "../../../../../../../components/z-index-boundary";
import { OverviewWidget } from "../../../../../../components/_overview-widget";
import { WidgetHeader } from "../../../../../../components/_widget";
import { CtxMenuItem } from "../../../../../../components/react/CtxMenu/CtxMenuItem";
import { CtxPopoverMenu } from "../../../../../../components/react/CtxMenu/CtxPopoverMenu";
import { queryUIConfig } from "../../../../../../queries";
import { useQuery } from "../../../../../../queries/react-query";

import { type ComponentReference, PinnedItemWidget } from "./pinned-item-widget";
import type { PinnedItemsSectionState } from "./pinned-items-section-state";

export interface PinnedItemsSectionProps<TItemData> {
	name: (count: number) => string;
	state: PinnedItemsSectionState<TItemData>;
	getCardTitle: (data: TItemData) => undefined | string;
	getCardDescription: (data: TItemData) => undefined | string;
	getCardComponentReference: (data: TItemData) => undefined | ComponentReference;
	onEnlarge: (data: TItemData) => void;
	onAddToDashboard?: (items: ReadonlyArray<TItemData>) => void;
	children: (data: TItemData) => React.ReactNode;
}

export const PinnedItemsSection = <TItemData,>({
	name,
	state,
	getCardTitle,
	getCardDescription,
	getCardComponentReference,
	children,
	onEnlarge,
	onAddToDashboard,
}: PinnedItemsSectionProps<TItemData>): null | React.ReactElement => {
	const { data: webUIConfig } = useQuery({
		...queryUIConfig,
		select: prop("webUIConfig"),
	});

	const items = useComputed((monitor) => state.getItems(monitor), [state]);
	const expanded = useComputed(state.isExpanded);

	if (items.length === 0) {
		return null;
	}

	return (
		<OverviewWidget
			data-testid="pinned-items-section"
			header={(props) => (
				<WidgetHeader {...props} className="sticky top-0 z-20 -mb-4 rounded-none bg-grey-200 py-4">
					<ZIndexBoundary zIndex={(closestZIndex = 0) => closestZIndex + 50}>
						{(zIndex) => (
							<CtxPopoverMenu zIndex={zIndex}>
								{webUIConfig?.featureFlags.dashboards && onAddToDashboard != null && (
									<CtxMenuItem
										label="Add all items to dashboard"
										onClick={() => onAddToDashboard(items.map(prop("data")) as Array<TItemData>)}
									/>
								)}
								<CtxMenuItem label="Unpin all items" onClick={() => state.unpinAll()} />
								<CtxMenuItem label="Collapse all items" onClick={() => state.expandAll(false)} />
								<CtxMenuItem label="Expand all items" onClick={() => state.expandAll(true)} />
							</CtxPopoverMenu>
						)}
					</ZIndexBoundary>
				</WidgetHeader>
			)}
			isExpandedByDefault={expanded}
			layout="plain"
			namespace={["pinned-list", "pinned-items"]}
			title={`${name(items.length)} (${items.length})`}
		>
			<div className="space-y-[3px]">
				{items.map(({ identifier, data, isExpanded }) => (
					<PinnedItemWidget
						component={getCardComponentReference(data)}
						description={getCardDescription(data)}
						isExpanded={isExpanded}
						key={identifier}
						name={name(1).toLowerCase()}
						onAddToDashboard={onAddToDashboard != null && (() => onAddToDashboard([data]))}
						onEnlarge={() => onEnlarge(data)}
						onUnpin={() => state.unpin(data)}
						title={getCardTitle(data)}
					>
						{children(data)}
					</PinnedItemWidget>
				))}
			</div>

			{webUIConfig?.featureFlags.dashboards && onAddToDashboard != null && (
				<Button
					className="mt-4 w-full uppercase"
					onClick={() => onAddToDashboard(items.map(prop("data")) as Array<TItemData>)}
					variant="tertiary"
				>
					Add all items to dashboard
				</Button>
			)}
		</OverviewWidget>
	);
};
